/**
 * Minimal client-side JWT inspection. We only read claims for routing decisions
 * (e.g. the MFA-verified gate). The server is the source of truth for authorization.
 */

export interface JwtClaims {
  sub?: string;
  exp?: number;
  iat?: number;
  /** Backend claim: true once the user has completed MFA on this token. */
  mfa?: boolean;
  /** Backend claim: true for admin users. */
  adm?: boolean;
  [key: string]: unknown;
}

export function decodeJwt(token: string): JwtClaims | null {
  const segments = token.split('.');
  if (segments.length !== 3) return null;
  const payload = segments[1];
  if (!payload) return null;
  try {
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padding = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
    const json = atob(padded + padding);
    return JSON.parse(json) as JwtClaims;
  } catch {
    return null;
  }
}

export function isMfaVerified(token: string | null | undefined): boolean {
  if (!token) return false;
  const claims = decodeJwt(token);
  return claims?.mfa === true;
}

export function expiresAtMs(token: string | null | undefined): number | null {
  if (!token) return null;
  const claims = decodeJwt(token);
  return typeof claims?.exp === 'number' ? claims.exp * 1000 : null;
}

export function isExpired(token: string | null | undefined, skewMs = 0): boolean {
  const exp = expiresAtMs(token);
  if (exp === null) return true;
  return Date.now() >= exp - skewMs;
}
